import React from 'react';
import { FormattedMessage } from 'react-intl';
import { of, concat, EMPTY } from 'rxjs';
import { mergeMap, map, catchError, filter } from 'rxjs/operators';
import type { Epic } from 'redux-observable';
import { ofType } from 'redux-observable';

import { cswSetPendingDocuments } from '../csw/csw.actions';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { messages } from '../../orders/order-details/fulfillment-plan/sections/customs-documents/messages';
import Global from '../global-selectors';
import * as Actions from './customs-documents.actions';
import * as Api from './customs-documents.api';
import type { UploadApiError } from './types';
import { normalizeDocuments } from './types';

type UploadEvent = { type: 'progress'; percentageComplete: number } | null;

const getUploadErrorMessage = (error: UploadApiError) =>
  error?.response?.data?.Errors?.[0]?.Message ?? error?.response?.data?.message ?? error?.message ?? 'Upload failed';

// ─── Carrier Capabilities ─────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy { getState } pattern
export const getCarrierDocumentCapabilitiesEpic: Epic = (action$, { getState }: any) =>
  action$.pipe(
    ofType(Actions.GET_CARRIER_DOCUMENT_CAPABILITIES_REQUEST),
    mergeMap(() => {
      const capabilities = Global.selectCustomsDocumentsCarrierCapabilities(getState());
      if (capabilities) return EMPTY;

      return Api.getCarrierDocumentCapabilities().pipe(
        map((caps) => Actions.getCarrierDocumentCapabilitiesSuccess(caps)),
        catchError((error) => of(Actions.getCarrierDocumentCapabilitiesFailure(error))),
      );
    }),
  );

// ─── Order Documents ──────────────────────────────────────────────────────────

export const getOrderDocumentsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(Actions.GET_ORDER_DOCUMENTS_REQUEST),
    mergeMap(({ salesOrderId }) =>
      Api.getOrderDocuments(salesOrderId).pipe(
        map((documents) => Actions.getOrderDocumentsSuccess(salesOrderId, normalizeDocuments(documents))),
        catchError((error) => of(Actions.getOrderDocumentsFailure(salesOrderId, error))),
      ),
    ),
  );

/**
 * Upload flow (2-step):
 * 1. POST to register → get { document_id, upload: { url, fields } }
 * 2. Multipart form POST to S3 presigned URL
 */
export const uploadOrderDocumentEpic: Epic = (action$) =>
  action$.pipe(
    ofType(Actions.UPLOAD_ORDER_DOCUMENT_REQUEST),
    mergeMap(({ salesOrderId, documentType, file }) => {
      const payload = {
        type: documentType,
        fileName: file.name,
        format: file.type === 'application/pdf' ? 'PDF' : 'PNG',
      };

      return Api.registerOrderDocument(salesOrderId, payload).pipe(
        mergeMap((registration) => {
          const documentId = registration.documentId ?? registration.DocumentId ?? registration.document_id;
          const upload = registration.upload ?? registration.Upload;
          return concat(
            of(
              Actions.uploadOrderDocumentSuccess(salesOrderId, {
                documentId,
                documentType,
                fileName: file.name,
                status: 'uploading',
              }),
            ),
            Api.uploadFileToS3(upload, file).pipe(
              mergeMap((rawEvent) => {
                const event = rawEvent as UploadEvent;
                if (event?.type === 'progress') {
                  return of(Actions.updateOrderUploadProgress(salesOrderId, event.percentageComplete));
                }
                return of(
                  Actions.updateOrderUploadProgress(salesOrderId, 100),
                  Actions.uploadOrderDocumentFileUploaded(salesOrderId, documentId),
                );
              }),
            ),
          );
        }),
        catchError((error) => {
          const errorMessage = getUploadErrorMessage(error);
          return of(
            Actions.uploadOrderDocumentSuccess(salesOrderId, {
              documentId: `failed-${crypto.randomUUID()}`,
              documentType,
              fileName: file.name,
              status: 'failed',
              error: errorMessage,
            }),
            Actions.uploadOrderDocumentFailure(salesOrderId, error),
          );
        }),
      );
    }),
  );

export const deleteOrderDocumentEpic: Epic = (action$) =>
  action$.pipe(
    ofType(Actions.DELETE_ORDER_DOCUMENT_REQUEST),
    mergeMap(({ salesOrderId, documentId }) =>
      Api.deleteOrderDocument(salesOrderId, documentId).pipe(
        map(() => Actions.deleteOrderDocumentSuccess(salesOrderId, documentId)),
        catchError((error) => {
          const msg = error?.response?.status === 409 ? messages.deleteDocumentConflict : messages.deleteDocumentError;
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: React.createElement(FormattedMessage, msg),
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
          return of(Actions.deleteOrderDocumentFailure(salesOrderId, documentId, error));
        }),
      ),
    ),
  );

// ─── Shipment Documents ───────────────────────────────────────────────────────

export const getShipmentDocumentsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(Actions.GET_SHIPMENT_DOCUMENTS_REQUEST),
    mergeMap(({ shipmentId }) =>
      Api.getShipmentDocuments(shipmentId).pipe(
        map((documents) => Actions.getShipmentDocumentsSuccess(shipmentId, normalizeDocuments(documents))),
        catchError((error) => of(Actions.getShipmentDocumentsFailure(shipmentId, error))),
      ),
    ),
  );

export const uploadShipmentDocumentEpic: Epic = (action$) =>
  action$.pipe(
    ofType(Actions.UPLOAD_SHIPMENT_DOCUMENT_REQUEST),
    mergeMap(({ shipmentId, documentType, file, carrierCode, trackingNumber }) => {
      const payload = {
        type: documentType,
        fileName: file.name,
        format: file.type === 'application/pdf' ? 'PDF' : 'PNG',
        carrierCode,
        trackingNumber,
      };

      return Api.registerShipmentDocument(shipmentId, payload).pipe(
        mergeMap((registration) => {
          const documentId = registration.documentId ?? registration.DocumentId ?? registration.document_id;
          const upload = registration.upload ?? registration.Upload;
          return concat(
            of(
              Actions.uploadShipmentDocumentSuccess(shipmentId, {
                documentId,
                documentType,
                fileName: file.name,
                status: 'uploading',
              }),
            ),
            Api.uploadFileToS3(upload, file).pipe(
              filter((rawEvent) => (rawEvent as UploadEvent)?.type !== 'progress'),
              mergeMap(() => of(Actions.uploadShipmentDocumentFileUploaded(shipmentId, documentId))),
            ),
          );
        }),
        catchError((error) => {
          const errorMessage = getUploadErrorMessage(error);
          return of(
            Actions.uploadShipmentDocumentSuccess(shipmentId, {
              documentId: `failed-${crypto.randomUUID()}`,
              documentType,
              fileName: file.name,
              status: 'failed',
              error: errorMessage,
            }),
            Actions.uploadShipmentDocumentFailure(shipmentId, error),
          );
        }),
      );
    }),
  );

export const deleteShipmentDocumentEpic: Epic = (action$) =>
  action$.pipe(
    ofType(Actions.DELETE_SHIPMENT_DOCUMENT_REQUEST),
    mergeMap(({ shipmentId, documentId }) =>
      Api.deleteShipmentDocument(shipmentId, documentId).pipe(
        map(() => Actions.deleteShipmentDocumentSuccess(shipmentId, documentId)),
        catchError((error) => {
          const msg = error?.response?.status === 409 ? messages.deleteDocumentConflict : messages.deleteDocumentError;
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: React.createElement(FormattedMessage, msg),
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
          return of(Actions.deleteShipmentDocumentFailure(shipmentId, documentId, error));
        }),
      ),
    ),
  );

export const sendShipmentDocumentsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(Actions.SEND_SHIPMENT_DOCUMENTS_REQUEST),
    mergeMap(({ shipmentId }) =>
      Api.sendShipmentDocuments(shipmentId).pipe(
        mergeMap(() => of(Actions.sendShipmentDocumentsSuccess(shipmentId), Actions.getShipmentDocumentsRequest(shipmentId))),
        catchError((error) => of(Actions.sendShipmentDocumentsFailure(shipmentId, error))),
      ),
    ),
  );

// ─── Pending Document Config ─────────────────────────────────────────────────

// Sets the pendingDocuments flag in CSW state only. The flag is persisted to the
// backend automatically when the user rates or purchases a label through the
// normal save-config-and-rate flow (buildSaveShippingConfigurationAndRateRequest
// includes customs.pendingDocuments). This avoids a separate saveConfig call that
// could race with the existing rating flow.
export const savePendingDocumentConfigEpic: Epic = (action$) =>
  action$.pipe(
    ofType(Actions.SAVE_PENDING_DOCUMENT_CONFIG_REQUEST),
    map(({ fulfillmentPlanId, pendingDocument }) => cswSetPendingDocuments(fulfillmentPlanId, pendingDocument)),
  );
